import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { dropshipProducts } from "@/mocks/dropship";
import FactoryAuthModal from "./FactoryAuthModal";
import FactoryProductPostModal from "./FactoryProductPostModal";
import MarketplaceOrderModal from "./MarketplaceOrderModal";
import MarketplaceMessageModal from "./MarketplaceMessageModal";
import DonationModal from "@/pages/community/components/DonationModal";

interface FactoryUser {
  name: string;
  email: string;
  phone?: string;
  company: string;
  country: string;
  flag: string;
  address: string;
}

interface MarketplaceProduct {
  id: number;
  name: string;
  price: number;
  minOrder: number;
  image: string;
  category: string;
  seller: string;
  sellerFlag: string;
  sellerCountry: string;
  factoryAddress: string;
  sold: number;
  commission: number;
  stock: number;
  description: string;
  rating: number;
  reviews: number;
}

const categories = ["All", "Electronics", "Health & Beauty", "Home & Garden", "Sports & Fitness", "Apparel", "Machinery", "Auto Parts"];

const extendedProducts: MarketplaceProduct[] = dropshipProducts.slice(0, 20).map((p, i) => ({
  ...p,
  minOrder: [1, 5, 10, 50, 100][i % 5],
  factoryAddress: [
    "No.88 Tianhe Industrial Park, Guangzhou, Guangdong, China",
    "Building 3, Longhua District, Shenzhen, Guangdong, China",
    "Zone B, Yiwu International Trade City, Zhejiang, China",
    "No.12 Nanjing Road, Foshan, Guangdong, China",
    "Anji Bamboo Industrial Zone, Huzhou, Zhejiang, China",
    "No.55 Suzhou Industrial Park, Suzhou, Jiangsu, China",
    "Nantong Textile Base, Nantong, Jiangsu, China",
    "No.9 Dongguan Tech Park, Dongguan, Guangdong, China",
    "Hangzhou E-Commerce Park, Hangzhou, Zhejiang, China",
    "No.22 Ningbo Port Zone, Ningbo, Zhejiang, China",
  ][i % 10],
  rating: parseFloat((4.2 + Math.random() * 0.7).toFixed(1)),
  reviews: Math.floor(120 + Math.random() * 2000),
}));

export default function FactoryMarketplace() {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<"popular" | "price_asc" | "price_desc" | "newest">("popular");
  const [showAuth, setShowAuth] = useState(false);
  const [factoryUser, setFactoryUser] = useState<FactoryUser | null>(null);
  const [showPostModal, setShowPostModal] = useState(false);
  const [orderProduct, setOrderProduct] = useState<MarketplaceProduct | null>(null);
  const [messageProduct, setMessageProduct] = useState<MarketplaceProduct | null>(null);
  const [showDonation, setShowDonation] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  const filtered = extendedProducts
    .filter(p => {
      const matchCat = activeCategory === "All" || p.category === activeCategory;
      const matchSearch = !searchQuery || p.name.toLowerCase().includes(searchQuery.toLowerCase()) || p.seller.toLowerCase().includes(searchQuery.toLowerCase());
      return matchCat && matchSearch;
    })
    .sort((a, b) => {
      if (sortBy === "price_asc") return a.price - b.price;
      if (sortBy === "price_desc") return b.price - a.price;
      if (sortBy === "newest") return b.id - a.id;
      return b.sold - a.sold;
    });

  const handleFactoryAuth = (user: FactoryUser) => {
    setFactoryUser(user);
    setShowAuth(false);
  };

  return (
    <section className="py-16 bg-[#fafaf8]">
      <div className="max-w-screen-2xl mx-auto px-4 md:px-8">

        {/* Section Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="w-1 h-6 bg-amber-400 rounded-full inline-block"></span>
              <p className="text-amber-500 text-sm font-bold uppercase tracking-widest">Factory Direct</p>
            </div>
            <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-1">
              2 Million+ Factory Products
            </h2>
            <p className="text-gray-500 text-sm">
              Direct from verified factories · Sample orders welcome · UPS tracked delivery
            </p>
            {/* Social stars row */}
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              <span className="text-xs text-gray-400 font-semibold">Powered by:</span>
              {[
                { icon: "ri-facebook-circle-fill", label: "Facebook", cls: "bg-sky-50 border-sky-200 text-sky-600" },
                { icon: "ri-tiktok-fill", label: "TikTok", cls: "bg-gray-900 text-white border-gray-800" },
                { icon: "ri-twitter-x-line", label: "Twitter", cls: "bg-gray-100 text-gray-700 border-gray-200" },
                { icon: "ri-youtube-fill", label: "YouTube", cls: "bg-red-50 border-red-200 text-red-600" },
              ].map(s => (
                <span key={s.label} className={`flex items-center gap-1 border text-xs font-bold px-2.5 py-1 rounded-full whitespace-nowrap ${s.cls}`}>
                  <i className={s.icon}></i> {s.label}
                </span>
              ))}
              <span className="text-xs text-amber-600 font-semibold whitespace-nowrap">Stars help factories sell fast!</span>
            </div>
          </div>

          {/* Factory CTA + Donation */}
          <div className="flex flex-wrap gap-3 shrink-0">
            {factoryUser ? (
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-full px-4 py-2">
                  <span className="text-lg">{factoryUser.flag}</span>
                  <div>
                    <p className="text-xs font-bold text-gray-800">{factoryUser.company}</p>
                    <p className="text-xs text-gray-400">{factoryUser.name}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowPostModal(true)}
                  className="flex items-center gap-1.5 bg-amber-400 hover:bg-amber-500 text-white text-sm font-bold px-5 py-2.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line"></i> Post Product
                </button>
                <button
                  onClick={() => setFactoryUser(null)}
                  className="text-xs text-gray-400 hover:text-gray-600 cursor-pointer whitespace-nowrap"
                >
                  Sign Out
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowAuth(true)}
                className="flex items-center gap-1.5 bg-amber-400 hover:bg-amber-500 text-white text-sm font-bold px-5 py-2.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-store-2-line"></i> Factory Sign Up / Login
              </button>
            )}
            <button
              onClick={() => setShowDonation(true)}
              className="flex items-center gap-1.5 bg-rose-50 hover:bg-rose-100 border border-rose-200 text-rose-600 text-sm font-bold px-5 py-2.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-heart-fill"></i> Donate for Infants
            </button>
          </div>
        </div>

        {/* Donation Banner */}
        <div className="relative rounded-2xl overflow-hidden mb-8 cursor-pointer" onClick={() => setShowDonation(true)}>
          <img
            src="https://public.readdy.ai/ai/img_res/cc8f900eed265cd322ad307568fe0a07.jpg"
            alt="Donate for World Infants"
            className="w-full h-36 md:h-44 object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-rose-900/80 via-rose-800/60 to-transparent"></div>
          <div className="absolute inset-0 flex items-center px-6 md:px-12">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <i className="ri-heart-fill text-rose-300 text-lg"></i>
                <span className="text-rose-200 text-xs font-bold uppercase tracking-widest">CrownGlobal Cares</span>
              </div>
              <h3 className="text-white text-xl md:text-2xl font-extrabold mb-1">when you are rich and sell container to many buyer ,and together make the small donation for the world infant, the world infant will show their thanksgiving for their whole life</h3>
              <p className="text-white/80 text-xs md:text-sm max-w-md">
                When sellers complete container orders, we welcome donations for infants who need love, milk &amp; clothes. Every purchase helps a child.
              </p>
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); setShowDonation(true); }}
              className="hidden md:flex items-center gap-2 bg-rose-400 hover:bg-rose-500 text-white font-bold px-6 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap shrink-0"
            >
              <i className="ri-heart-fill"></i> Donate Now
            </button>
          </div>
        </div>

        {/* Search + Filter Bar */}
        <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
              <input
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search products, factories, categories..."
                className="w-full border border-gray-200 rounded-xl pl-10 pr-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors bg-white"
              />
            </div>
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value as typeof sortBy)}
              className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 cursor-pointer bg-white"
            >
              <option value="popular">Best Selling</option>
              <option value="price_asc">Price: Low to High</option>
              <option value="price_desc">Price: High to Low</option>
              <option value="newest">Newest First</option>
            </select>
            <div className="flex items-center gap-1 border border-gray-200 rounded-xl p-1">
              <button
                onClick={() => setViewMode("grid")}
                className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${viewMode === "grid" ? "bg-amber-400 text-white" : "text-gray-400 hover:text-gray-600"}`}
              >
                <i className="ri-grid-line text-sm"></i>
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${viewMode === "list" ? "bg-amber-400 text-white" : "text-gray-400 hover:text-gray-600"}`}
              >
                <i className="ri-list-check text-sm"></i>
              </button>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`text-xs font-semibold px-4 py-1.5 rounded-full border transition-all cursor-pointer whitespace-nowrap ${
                  activeCategory === cat
                    ? "bg-amber-400 text-white border-amber-400"
                    : "bg-white text-gray-500 border-gray-200 hover:border-amber-300"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        <p className="text-xs text-gray-400 mb-4">{filtered.length} products found</p>

        {/* Product Grid */}
        {viewMode === "grid" ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 mb-8" data-product-shop>
            {filtered.map(p => (
              <ProductCard
                key={p.id}
                product={p}
                onOrder={() => setOrderProduct(p)}
                onMessage={() => setMessageProduct(p)}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-3 mb-8" data-product-shop>
            {filtered.map(p => (
              <ProductListRow
                key={p.id}
                product={p}
                onOrder={() => setOrderProduct(p)}
                onMessage={() => setMessageProduct(p)}
              />
            ))}
          </div>
        )}

        {/* View All CTA */}
        <div className="text-center">
          <button
            onClick={() => navigate("/overseas-warehouse")}
            className="inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-500 text-white font-bold px-10 py-4 rounded-full transition-colors cursor-pointer whitespace-nowrap text-base"
          >
            <i className="ri-store-2-line"></i>
            View All 2 Million+ Factory Products
            <i className="ri-arrow-right-line"></i>
          </button>
          <p className="text-gray-400 text-xs mt-3">UPS shipping · Real-time tracking · Buyer-seller messaging · Sample orders welcome</p>
        </div>
      </div>

      {/* Modals */}
      {showAuth && (
        <FactoryAuthModal
          onClose={() => setShowAuth(false)}
          onAuth={handleFactoryAuth}
        />
      )}
      {showPostModal && factoryUser && (
        <FactoryProductPostModal
          factoryUser={factoryUser}
          onClose={() => setShowPostModal(false)}
        />
      )}
      {orderProduct && (
        <MarketplaceOrderModal
          product={orderProduct}
          onClose={() => setOrderProduct(null)}
        />
      )}
      {messageProduct && (
        <MarketplaceMessageModal
          product={messageProduct}
          onClose={() => setMessageProduct(null)}
        />
      )}
      {showDonation && (
        <DonationModal onClose={() => setShowDonation(false)} />
      )}
    </section>
  );
}

// ─── Product Card (Grid) ───────────────────────────────────────────────────────
function ProductCard({ product, onOrder, onMessage }: {
  product: MarketplaceProduct;
  onOrder: () => void;
  onMessage: () => void;
}) {
  return (
    <div className="bg-white border border-gray-100 rounded-xl overflow-hidden group hover:-translate-y-1 transition-transform duration-300">
      <div className="w-full h-44 overflow-hidden relative cursor-pointer" onClick={onOrder}>
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="p-3">
        <span className="text-xs bg-gray-100 text-gray-500 font-semibold px-2 py-0.5 rounded-full">{product.category}</span>
        <h3
          className="font-semibold text-gray-800 text-sm mt-2 mb-1 line-clamp-2 cursor-pointer hover:text-amber-500 transition-colors"
          onClick={onOrder}
        >
          {product.name}
        </h3>

        {/* Rating */}
        <div className="flex items-center gap-1 mb-1.5">
          <div className="flex items-center gap-0.5">
            {[1,2,3,4,5].map(s => (
              <i key={s} className={s <= Math.round(product.rating) ? "ri-star-fill text-amber-400 text-xs" : "ri-star-line text-amber-400 text-xs"}></i>
            ))}
          </div>
          <span className="text-xs text-gray-400">({product.reviews.toLocaleString()})</span>
        </div>

        {/* Factory address */}
        <div className="flex items-start gap-1 mb-2">
          <i className="ri-map-pin-line text-amber-400 text-xs mt-0.5 shrink-0"></i>
          <span className="text-xs text-gray-400 line-clamp-1">{product.factoryAddress}</span>
        </div>

        <div className="flex items-center justify-between mb-1">
          <span className="text-amber-500 font-extrabold text-lg">${product.price}</span>
          <span className="text-xs text-gray-400">{product.sold.toLocaleString()} sold</span>
        </div>
        <p className="text-xs text-gray-400 mb-3">Min. order: {product.minOrder} pc{product.minOrder > 1 ? "s" : ""}</p>

        <div className="flex gap-1.5">
          <button
            onClick={onOrder}
            className="flex-1 bg-amber-400 hover:bg-amber-500 text-white text-xs font-bold py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-shopping-cart-line mr-1"></i>Sample Order
          </button>
          <button
            onClick={onMessage}
            className="w-8 h-8 flex items-center justify-center border border-gray-200 hover:border-amber-300 text-gray-500 hover:text-amber-500 rounded-full transition-colors cursor-pointer shrink-0"
            title="Message seller"
          >
            <i className="ri-message-3-line text-xs"></i>
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Product List Row ──────────────────────────────────────────────────────────
function ProductListRow({ product, onOrder, onMessage }: {
  product: MarketplaceProduct;
  onOrder: () => void;
  onMessage: () => void;
}) {
  return (
    <div className="bg-white border border-gray-100 rounded-xl overflow-hidden flex gap-4 p-4 hover:border-amber-200 transition-colors">
      <div className="w-28 h-28 rounded-xl overflow-hidden shrink-0 cursor-pointer" onClick={onOrder}>
        <img src={product.image} alt={product.name} className="w-full h-full object-cover object-top" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2 mb-1">
          <h3 className="font-semibold text-gray-800 text-sm cursor-pointer hover:text-amber-500 transition-colors line-clamp-2" onClick={onOrder}>
            {product.name}
          </h3>
          <span className="text-amber-500 font-extrabold text-lg shrink-0">${product.price}</span>
        </div>
        <div className="flex items-center gap-1 mb-1.5">
          {[1,2,3,4,5].map(s => (
            <i key={s} className={s <= Math.round(product.rating) ? "ri-star-fill text-amber-400 text-xs" : "ri-star-line text-amber-400 text-xs"}></i>
          ))}
          <span className="text-xs text-gray-400 ml-1">({product.reviews.toLocaleString()} reviews)</span>
        </div>
        <div className="flex items-center gap-1 mb-1">
          <i className="ri-map-pin-line text-amber-400 text-xs shrink-0"></i>
          <span className="text-xs text-gray-500">{product.factoryAddress}</span>
        </div>
        <div className="flex items-center gap-3 text-xs text-gray-400 mb-3">
          <span>{product.sellerFlag} {product.seller}</span>
          <span>·</span>
          <span>Min. {product.minOrder} pc{product.minOrder > 1 ? "s" : ""}</span>
          <span>·</span>
          <span>{product.sold.toLocaleString()} sold</span>
        </div>
        <div className="flex gap-2">
          <button
            onClick={onOrder}
            className="bg-amber-400 hover:bg-amber-500 text-white text-xs font-bold px-4 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-shopping-cart-line mr-1"></i>Sample Order
          </button>
          <button
            onClick={onMessage}
            className="flex items-center gap-1.5 border border-gray-200 hover:border-amber-300 text-gray-500 hover:text-amber-500 text-xs font-semibold px-4 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-message-3-line"></i> Message Seller
          </button>
        </div>
      </div>
    </div>
  );
}
